/**
 * READMETemplatePage — /portfolio-kit/readme-template
 *
 * Prescriptive GitHub README template. Free tier sees outline + purpose per section.
 * All Access unlocks the full fill-in-the-blanks template bodies + copy buttons.
 */

import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockIcon from "@mui/icons-material/Lock";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { README_SECTIONS } from "../data/portfolioKit";
import { L } from "../utils/localized";
import { useAuth } from "../hooks/useAuth";
import { getTierAccess } from "../utils/tier";
import ProUpsellNote from "../components/ProUpsellNote";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

export default function READMETemplatePage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation("pages-account");
  const tierAccess = getTierAccess(user);
  const isAllAccess = tierAccess.canAccessProducts || tierAccess.isModerator;
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const sections = L(README_SECTIONS, i18n.language);

  const handleCopy = (text: string, id: number) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleCopyAll = () => {
    const full = sections
      .filter((s) => s.tierGate === "free" || isAllAccess)
      .map((s) => s.templateBody)
      .join("\n\n---\n\n");
    navigator.clipboard.writeText(full);
    setCopiedId(-1);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          py: { xs: 5, md: 7 },
        }}
      >
        <Container maxWidth="lg">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/portfolio-kit")}
            sx={{ color: theme.palette.primary.main, mb: 2 }}
          >
            {t("READMETemplatePage.backToPortfolioKitButton")}
          </Button>
          <Chip
            label={t("READMETemplatePage.heroChip")}
            size="small"
            sx={{
              fontWeight: 500,
              fontSize: "0.72rem",
              letterSpacing: "0.08em",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              mb: 2,
            }}
          />
          <Typography variant="h3" fontWeight={500} sx={{ letterSpacing: "-0.02em", mb: 1.5 }}>
            {t("READMETemplatePage.heading")}
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, maxWidth: 820, lineHeight: 1.5 }}>
            {t("READMETemplatePage.subheading")}
          </Typography>
          {isAllAccess && (
            <Box mt={3}>
              <Button
                variant="contained"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyAll}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  fontWeight: 500,
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) },
                }}
              >
                {copiedId === -1 ? t("READMETemplatePage.copiedFullTemplate") : t("READMETemplatePage.copyFullTemplate")}
              </Button>
            </Box>
          )}
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Stack spacing={3}>
          {sections.map((section) => {
            const isGated = section.tierGate === "all_access" && !isAllAccess;
            return (
              <Paper
                key={section.number}
                sx={{
                  p: 0,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                {/* Section header */}
                <Box
                  sx={{
                    p: 3,
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap" gap={0.5}>
                        <Chip
                          label={`#${section.number}`}
                          size="small"
                          sx={{ bgcolor: theme.palette.primary.main, color: theme.palette.background.paper, fontWeight: 500, fontSize: "0.7rem" }}
                        />
                        <Chip
                          label={section.required ? t("READMETemplatePage.requiredChip") : t("READMETemplatePage.optionalChip")}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.65rem",
                            bgcolor: section.required ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.text.secondary, 0.1),
                            color: section.required ? theme.palette.error.main : theme.palette.text.secondary,
                          }}
                        />
                        <Chip
                          label={section.tierGate === "free" ? t("READMETemplatePage.freeChip") : t("READMETemplatePage.allAccessChip")}
                          icon={section.tierGate === "all_access" ? <LockIcon sx={{ fontSize: 12 }} /> : undefined}
                          size="small"
                          sx={{
                            fontWeight: 500,
                            fontSize: "0.65rem",
                            bgcolor: section.tierGate === "free" ? alpha(theme.palette.success.main, 0.12) : alpha(theme.palette.primary.main, 0.12),
                            color: section.tierGate === "free" ? theme.palette.success.dark : theme.palette.primary.main,
                          }}
                        />
                      </Stack>
                      <Typography variant="h5" fontWeight={500} sx={{ letterSpacing: "-0.01em" }}>
                        {section.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        {section.purpose}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* Section body */}
                <Box sx={{ p: 3 }}>
                  <Typography variant="body2" color="text.secondary" mb={2} sx={{ lineHeight: 1.7 }}>
                    <strong>{t("READMETemplatePage.whatToShowLabel")}</strong> {section.freeSummary}
                  </Typography>

                  {/* Template body — gated */}
                  {isGated ? (
                    tierAccess.isPro ? <ProUpsellNote /> : (
                      <Paper
                        sx={{
                          p: 3,
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          border: `1px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
                          textAlign: "center",
                          borderRadius: 2,
                        }}
                      >
                        <LockIcon sx={{ color: theme.palette.primary.main, fontSize: 28, mb: 1 }} />
                        <Typography variant="body2" fontWeight={500} mb={0.5}>
                          {t("READMETemplatePage.lockedHeading")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                          {t("READMETemplatePage.lockedBody")}
                        </Typography>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => navigate("/pricing")}
                          sx={{ bgcolor: theme.palette.primary.main, fontWeight: 500, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}
                        >
                          {t("READMETemplatePage.unlockAllAccessButton")}
                        </Button>
                      </Paper>
                    )
                  ) : (
                    <Paper
                      sx={{
                        p: 0,
                        bgcolor: theme.palette.background.paper,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          px: 2,
                          py: 1,
                          bgcolor: alpha(theme.palette.background.paper, 0.05),
                          borderBottom: `1px solid ${alpha(theme.palette.background.paper, 0.1)}`,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: alpha(theme.palette.background.paper, 0.6), fontFamily: "monospace" }}>
                          {t("READMETemplatePage.sectionFileName", { number: section.number })}
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                          onClick={() => handleCopy(section.templateBody, section.number)}
                          sx={{ color: alpha(theme.palette.background.paper, 0.8), textTransform: "none", fontSize: "0.75rem" }}
                        >
                          {copiedId === section.number ? t("READMETemplatePage.copiedButton") : t("READMETemplatePage.copyButton")}
                        </Button>
                      </Stack>
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          p: 2,
                          color: theme.palette.success.light,
                          fontSize: "0.82rem",
                          fontFamily: "'JetBrains Mono', Menlo, monospace",
                          overflow: "auto",
                          lineHeight: 1.55,
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {section.templateBody}
                      </Box>
                    </Paper>
                  )}

                  {/* Rationale + mistakes */}
                  <Box mt={3}>
                    <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.04), border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                      <Typography variant="caption" fontWeight={500} sx={{ color: theme.palette.success.main, letterSpacing: "0.08em" }}>
                        {t("READMETemplatePage.whySectionExistsLabel")}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6 }}>
                        {section.rationale}
                      </Typography>
                    </Paper>
                    <Paper sx={{ p: 2, mt: 2, bgcolor: alpha(theme.palette.error.main, 0.04), border: `1px solid ${alpha(theme.palette.error.main, 0.2)}` }}>
                      <Typography variant="caption" fontWeight={500} sx={{ color: theme.palette.error.main, letterSpacing: "0.08em" }}>
                        {t("READMETemplatePage.commonMistakesLabel")}
                      </Typography>
                      <Stack spacing={0.5} mt={1}>
                        {section.commonMistakes.map((m) => (
                          <Typography key={m} variant="body2" sx={{ lineHeight: 1.5 }}>
                            • {m}
                          </Typography>
                        ))}
                      </Stack>
                    </Paper>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Stack>

        {/* Footer CTA */}
        <Divider sx={{ my: 4 }} />
        <Paper sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.05), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, textAlign: "center" }}>
          <Typography variant="h6" fontWeight={500} mb={1}>
            {t("READMETemplatePage.footerHeading")}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {t("READMETemplatePage.footerBody")}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
            <Button
              variant="contained"
              onClick={() => navigate("/portfolio-kit/deployment-checklist")}
              sx={{ bgcolor: theme.palette.primary.main, fontWeight: 500, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}
            >
              {t("READMETemplatePage.nextDeploymentChecklistButton")}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/portfolio-kit")}
              sx={{ borderColor: alpha(theme.palette.primary.main, 0.5), color: theme.palette.primary.main }}
            >
              {t("READMETemplatePage.backToPortfolioKitButton")}
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
